import React, { useContext } from 'react';

// DEPENDENCIES
import { Row, Col } from 'react-bootstrap';

// GLOBAL VARIABLES
import { DELIVERY_LOG_TYPE } from 'codes.js';
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE } from 'pathnames.js';
import { SORT_ORDER } from 'globals.js';

// LOCAL VARIABLES
import { ACTIONS, COLUMNS } from '../../../../columns.js';
import { DELIVERED_TOTALS_COLUMNS } from './variables.js';

// GLOBAL HELPERS
import { getListData, ListContext } from 'helpers/getListData';

// CORE COMPONENTS
import Form from 'core/form/Form';

// PARTS
import Main from 'parts/main/Main';
import Table, { Filters } from 'parts/table/Table.js';
import Title from 'parts/title/Title';

// LOCAL COMPONENTS
import ExcelButtons from '../../tools/ExcelButtons';
import TotalTable from './parts/TotalTable.js';
import OpenTasksCell from './parts/OpenTasksCell.js';

// MAIN COMPONENT
const Delivered = () => {
  const { data, preferences } = useContext(ListContext) || {};

  return (
    <Main>
      <Title
        documentTitle="Delivery Log -> Delivered"
        title="Delivered"
        tools={<ExcelButtons fileName="delivery_log_delivered" />}
      />
      <Filters>
        <Filters.Date.Range />
        <Filters.Button.SalesType />
        <Filters.Button.NewUsed />
        <Filters.Search />
        <Table.Columns />
      </Filters>
      <Table
        heading={
          <span>
            Results for "Delivery Log -{' '}
            {preferences?.filters?.salesType === 'R'
              ? 'Retail - '
              : preferences?.filters?.salesType === 'W'
              ? 'Wholesale - '
              : ''}
            {preferences?.filters?.newUsed === '1'
              ? `New (${data?.new_records})`
              : preferences?.filters?.newUsed === '0'
              ? `Pre-Owned (${data?.preowned_records})`
              : `All (${data?.total_records})`}
            " | Backouts from Previous Months (<span className="text-danger">{data?.total_backout_records}</span>)
          </span>
        }
        mobilePreviewKeys={['customer1_name', 'delivered_date', 'vehicle']}
        className="flex-grow-0 mb-0"
        autoSizeHeight={false}
        showNumber
      />
      <Row className="justify-content-end">
        <Col xs={24}>
          <TotalTable columns={DELIVERED_TOTALS_COLUMNS} className="w-auto fw-bolder" />
        </Col>
      </Row>
    </Main>
  );
};

// EXPORT
export default getListData(Delivered, {
  key: 'deliveryLogDelivered',
  fetchArgs: {
    endpoint: ENDPOINTS.deliveryLog.list,
    params: {
      deliveryLogType: DELIVERY_LOG_TYPE.Delivered.code
    }
  },
  defaults: {
    startDate: Form.Date.OPTIONS.thisMonth.startDate,
    endDate: Form.Date.OPTIONS.thisMonth.endDate,
    salesType: 'R',
    order: SORT_ORDER.ascending,
    sort: 'delivered_date',
    startRow: null
  },
  columns: [
    COLUMNS.deliveredDate,
    {
      ...COLUMNS.customer(),
      key: 'customer1_name',
      key2: 'customer2_name',
      width: 80
    },
    {
      ...COLUMNS.ageOfVehicle,
      key: 'age'
    },
    COLUMNS.newUsed,
    COLUMNS.saleSubtype,
    COLUMNS.purchaseLease,
    {
      ...COLUMNS.retailWholesale,
      key: 'sale_type'
    },
    COLUMNS.stockNumber,
    {
      ...COLUMNS.makeModel,
      key: 'vehicle',
      width: 90
    },
    {
      ...COLUMNS.trades,
      key: 'aryTrade'
    },
    COLUMNS.frontendGrossBP,
    COLUMNS.amGross,
    COLUMNS.fiGross,
    COLUMNS.backendGrossBP,
    COLUMNS.totalGrossBP,
    COLUMNS.dealerPack,
    COLUMNS.totalGrossAP,
    {
      ...COLUMNS.salesPerson,
      key: 'salesperson1',
      key2: 'salesperson2'
    },
    COLUMNS.salesManager,
    COLUMNS.fmManager,
    {
      component: OpenTasksCell,
      key: 'total_open_tasks',
      label: 'Tasks',
      width: 55,
      tooltip: 'Open Tasks'
    }
  ],
  actions: [ACTIONS.open(INTERFACE.sales, 'sales_id')],
  filterColumns: true,
  sortable: true
});
